<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { TransactionData } from '@shared/types/base'
import { useStore } from 'vuex'
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore'
import { dbRegistros, auth } from '@/firebase/config'
import { formatDate } from '@/utils/dateFormatter'
import { convertFirestoreTimestamp, formatPrice } from '@/components/utils/functions'

const store = useStore()
const currentInstitution = computed(() => store.getters['global/currentInstitution'])
const loading = ref(false)
const error = ref<string | null>(null)
const transacciones = ref<(TransactionData & { id: string })[]>([])
const columns = ref([
	{
		key: 'packageId',
		label: 'ID del paquete',
		sortable: true
	},
	{
		key: 'quantityDocuments',
		label: 'Cantidad de documentos',
		sortable: true
	},
	{
		key: 'userId',
		label: 'ID del usuario',
		sortable: true
	},
	{
		key: 'dateModification',
		label: 'Fecha de modificación',
		sortable: true
	},
	{
		key: 'environment',
		label: 'Ambiente',
		sortable: true
	},
	{
		key: 'pricePerDocument',
		label: 'Precio por documento',
		sortable: true
	},
	{
		key: 'details',
		label: 'Detalles',
		sortable: true
	},
	{
		key: 'typeTransaction',
		label: 'Tipo de transacción',
		sortable: true
	}
])

enum EEnvironment {
	production = 'Producción',
	test = 'Prueba'
}

/**
 * @description Obtiene las transacciones desde Firestore
 *
 * Con Custom Claims, las reglas de Firestore verifican automáticamente
 * si el usuario tiene permisos de administrador. Si el usuario no es admin,
 * Firestore rechazará la petición con permission-denied.
 */
const obtenerTransacciones = async (idInstitution: string, limitCount: number = 50): Promise<(TransactionData & { id: string })[]> => {
	try {
		loading.value = true
		error.value = null

		if (!idInstitution) {
			console.warn('No se proporcionó ID de institución')
			return []
		}

		// Verificar que el usuario esté autenticado
		const currentUser = auth.currentUser
		if (!currentUser) {
			error.value = 'Error de autenticación. Por favor, inicia sesión nuevamente'
			return []
		}

		// Refrescar el token para asegurar que tenemos los custom claims actualizados
		// Esto es importante cuando el admin cambia en Realtime Database
		// El true fuerza el refresco del token
		const token = await currentUser.getIdToken(true)

		// Diagnóstico: Verificar si el Custom Claim está presente en el token
		try {
			// Decodificar el token para verificar los custom claims
			const base64Url = token.split('.')[1]
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split('')
					.map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
					.join('')
			)
			const decodedToken = JSON.parse(jsonPayload)

			if (decodedToken.role?.tipoRole !== 'ADMINISTRADOR') {
				console.warn('No tienes permisos para acceder a las transacciones. Se requieren permisos de administrador.')
			}
		} catch (err) {
			console.error('Error al decodificar el token:', err)
		}

		// Acceder directamente a Firestore
		// Las reglas de Firestore verifican automáticamente el custom claim 'admin'
		// Si el usuario no es admin, Firestore rechazará con permission-denied
		const transaccionesRef = collection(dbRegistros, 'transacciones')
		const q = query(transaccionesRef, where('idInstitution', '==', idInstitution), orderBy('dateModification', 'desc'), limit(limitCount))

		const querySnapshot = await getDocs(q)

		if (querySnapshot.empty) {
			return []
		}

		// Convertir los documentos a objetos TransactionData
		const transaccionesData: (TransactionData & { id: string })[] = []
		querySnapshot.forEach(doc => {
			const data = doc.data() as TransactionData
			const transaccion = {
				id: doc.id,
				...data,
				// Convertir el Timestamp de Firestore a timestamp de JavaScript
				dateModification: convertFirestoreTimestamp(data.dateModification)
			}
			transaccionesData.push(transaccion)
		})

		return transaccionesData
	} catch (err: any) {
		console.error('Error al obtener transacciones:', err)

		if (err.code === 'permission-denied') {
			error.value = 'No tienes permisos para acceder a las transacciones. Se requieren permisos de administrador. Verifica la consola para más detalles.'
		} else if (err.code === 'unauthenticated') {
			error.value = 'Error de autenticación. Por favor, inicia sesión nuevamente'
		} else if (err.code === 'failed-precondition') {
			error.value = 'Error: Se requiere un índice en Firestore. Contacta al administrador.'
		} else {
			error.value = err.message || 'Error al cargar las transacciones'
		}

		return []
	} finally {
		loading.value = false
	}
}

// Cargar transacciones cuando el componente se monte
onMounted(() => {
	if (currentInstitution.value) {
		obtenerTransacciones(currentInstitution.value)
			.then(data => {
				transacciones.value = data
			})
			.catch(err => {
				console.error('Error al obtener transacciones:', err)
			})
	}
})

// Observar cambios en la institución actual
watch(
	() => currentInstitution.value,
	newInstitution => {
		if (newInstitution) {
			obtenerTransacciones(newInstitution)
				.then(data => {
					transacciones.value = data
				})
				.catch(err => {
					console.error('Error al obtener transacciones:', err)
				})
		} else {
			transacciones.value = []
		}
	}
)
</script>
<template>
	<div class="transacciones-container">
		<!-- Estado de carga -->
		<div v-if="loading" class="loading-state">
			<div class="loader"></div>
			<p class="loading-text">Cargando transacciones...</p>
		</div>

		<!-- Estado de error -->
		<div v-else-if="error" class="error-state">
			<va-icon name="error" class="error-icon" />
			<h3 class="error-title">Error al cargar datos</h3>
			<p class="error-description">{{ error }}</p>
		</div>

		<!-- Contenido principal -->
		<va-card v-else class="transacciones-card" bordered>
			<va-card-title class="header-gradient">
				<div class="header-content">
					<div class="header-main">
						<div class="header-icon-wrapper">
							<va-icon name="receipt_long" class="header-icon" />
						</div>
						<div class="header-text">
							<h2 class="header-title">Transacciones</h2>
							<p class="header-subtitle">{{ transacciones.length }} transacción{{ transacciones.length !== 1 ? 'es' : '' }} registrada{{ transacciones.length !== 1 ? 's' : '' }}</p>
						</div>
					</div>
				</div>
			</va-card-title>

			<va-card-content class="table-content">
				<div v-if="transacciones.length === 0" class="empty-state">
					<div class="empty-icon-wrapper">
						<va-icon name="info" class="empty-icon" />
					</div>
					<h3 class="empty-title">No hay transacciones</h3>
					<p class="empty-description">No se encontraron transacciones para esta institución</p>
				</div>

				<div v-else class="table-wrapper">
					<div class="table-scroll-container">
						<VaDataTable :items="transacciones" :columns="columns" :loading="loading" class="scrollable-table">
							<template #cell(typeTransaction)="{ rowData }">
								<div class="transaction-type-cell">
									<span :class="['type-badge', rowData.typeTransaction === 'aumento' ? 'type-increase' : 'type-decrease']">
										<va-icon :name="rowData.typeTransaction === 'aumento' ? 'trending_up' : 'trending_down'" class="type-icon" />
										{{ rowData.typeTransaction === 'aumento' ? 'Aumento' : 'Decremento' }}
									</span>
								</div>
							</template>

							<template #cell(environment)="{ rowData }">
								<div class="environment-cell">
									<span :class="['environment-badge', rowData.environment === 'production' ? 'env-production' : 'env-test']">
										<va-icon :name="rowData.environment === 'production' ? 'check_circle' : 'warning'" class="env-icon" />
										{{ EEnvironment[rowData.environment] }}
									</span>
								</div>
							</template>

							<template #cell(dateModification)="{ rowData }">
								<div class="date-cell">
									<va-icon name="event" class="date-icon" />
									<span class="date-text">{{ formatDate(rowData.dateModification) }}</span>
								</div>
							</template>

							<template #cell(pricePerDocument)="{ rowData }">
								<div class="price-cell">
									<span v-if="rowData.pricePerDocument" class="price-text">
										{{ formatPrice(rowData.pricePerDocument) }}
									</span>
									<span v-else class="price-empty">-</span>
								</div>
							</template>

							<template #cell(quantityDocuments)="{ rowData }">
								<div class="quantity-cell">
									<span class="quantity-text">{{ rowData.quantityDocuments || 0 }}</span>
								</div>
							</template>

							<template #cell(details)="{ rowData }">
								<div class="details-cell">
									<span class="details-text" :title="rowData.details">{{ rowData.details || '-' }}</span>
								</div>
							</template>
						</VaDataTable>
					</div>
				</div>
			</va-card-content>
		</va-card>
	</div>
</template>
<style scoped>
.transacciones-container {
	display: flex;
	justify-content: center;
	align-items: flex-start;
	padding: 1rem;
	margin: 0 auto;
	background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
	box-sizing: border-box;
	min-height: calc(100vh - 200px);
	height: 100%;
	overflow: hidden;
}

.transacciones-card {
	border-radius: 20px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
	background: white;
	border: none;
	width: 100%;
	max-width: 100%;
	display: flex;
	flex-direction: column;
	min-height: 0;
}

/* Header mejorado */
.header-gradient {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	padding: 0.5rem;
	border-radius: 0;
	position: relative;
	overflow: hidden;
	flex-shrink: 0;
}

.header-gradient::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.header-icon-wrapper {
	width: 34px;
	height: 34px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.2);
	backdrop-filter: blur(10px);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
}

.header-icon-wrapper:hover {
	transform: scale(1.1) rotate(5deg);
	background: rgba(255, 255, 255, 0.3);
}

.header-icon {
	color: white;
	font-size: 2rem;
}

.header-text {
	flex: 1;
}

.header-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: white;
	margin: 0;
	text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
	letter-spacing: -0.5px;
}

.header-subtitle {
	font-size: 1rem;
	color: rgba(255, 255, 255, 0.9);
	margin: 0.5rem 0 0 0;
	font-weight: 500;
}

.table-content {
	padding: 1rem;
	background: #fafbfc;
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
}

.table-wrapper {
	border-radius: 12px;
	background: white;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	height: 670px;
}

.table-scroll-container {
	overflow-y: auto;
	overflow-x: auto;
	flex: 1;
	min-height: 0;
	height: 100%;
}

/* Estilos de la tabla */
.scrollable-table {
	width: 100%;
	height: auto;
}

:deep(.scrollable-table .va-data-table) {
	width: 100%;
	height: auto;
}

:deep(.scrollable-table .va-data-table__table-wrapper) {
	overflow: visible !important;
	height: auto !important;
}

:deep(.scrollable-table table) {
	width: 100%;
}

:deep(.scrollable-table .va-data-table__table) {
	background: transparent;
	width: 100%;
}

:deep(.scrollable-table .va-data-table__table thead) {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	position: sticky;
	top: 0;
	z-index: 10;
}

:deep(.scrollable-table .va-data-table__table thead th) {
	padding: 1rem 1.25rem;
	font-weight: 700;
	color: #1a202c;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	border-bottom: 2px solid #e2e8f0;
}

:deep(.scrollable-table .va-data-table__table tbody tr) {
	transition: all 0.2s ease;
	border-bottom: 1px solid #f1f5f9;
}

:deep(.scrollable-table .va-data-table__table tbody tr:hover) {
	background: linear-gradient(90deg, rgba(37, 99, 235, 0.05) 0%, rgba(37, 99, 235, 0.02) 100%);
	transform: translateX(2px);
	box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1);
}

:deep(.scrollable-table .va-data-table__table tbody td) {
	padding: 1.25rem 1.25rem;
	color: #475569;
	font-size: 0.9375rem;
	vertical-align: middle;
}

/* Badges para tipo de transacción */
.transaction-type-cell {
	display: flex;
	align-items: center;
}

.type-badge {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 1rem;
	border-radius: 8px;
	font-weight: 600;
	font-size: 0.875rem;
	transition: all 0.2s ease;
}

.type-increase {
	background: linear-gradient(135deg, #10b981 0%, #059669 100%);
	color: white;
	box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.type-decrease {
	background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
	color: white;
	box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.type-icon {
	font-size: 1rem;
}

/* Badges para ambiente */
.environment-cell {
	display: flex;
	align-items: center;
}

.environment-badge {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 1rem;
	border-radius: 8px;
	font-weight: 600;
	font-size: 0.875rem;
	transition: all 0.2s ease;
}

.env-production {
	background: #062342;
	color: white;
	box-shadow: 0 2px 8px rgba(062342, 0.3);
}

.env-test {
	background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
	color: white;
	box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.env-icon {
	font-size: 1rem;
}

/* Celda de fecha */
.date-cell {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.date-icon {
	color: #062342;
	font-size: 1.125rem;
}

.date-text {
	color: #475569;
	font-weight: 500;
}

/* Celda de precio */
.price-cell {
	display: flex;
	align-items: center;
}

.price-text {
	color: #059669;
	font-weight: 700;
	font-size: 1rem;
}

.price-empty {
	color: #94a3b8;
	font-style: italic;
}

/* Celda de cantidad */
.quantity-cell {
	display: flex;
	align-items: center;
	justify-content: center;
}

.quantity-text {
	background: #062342;
	padding: 0.375rem 0.75rem;
	border-radius: 6px;
	font-weight: 700;
	color: #ffffff;
	font-size: 0.9375rem;
}

/* Celda de detalles */
.details-cell {
	max-width: 300px;
}

.details-text {
	color: #475569;
	font-size: 0.875rem;
	display: block;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* Estado vacío */
.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
}

.empty-icon-wrapper {
	width: 120px;
	height: 120px;
	border-radius: 50%;
	background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 2rem;
	animation: float 3s ease-in-out infinite;
}

.empty-icon {
	font-size: 4rem;
	color: #062342;
}

.empty-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: #1a202c;
	margin-bottom: 0.75rem;
	letter-spacing: -0.5px;
}

.empty-description {
	color: #718096;
	font-size: 1.125rem;
	font-weight: 500;
}

/* Estado de carga */
.loading-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
	background: white;
	border-radius: 20px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
}

.loader {
	border: 4px solid #f3f4f6;
	border-radius: 50%;
	border-top: 4px solid #062342;
	width: 60px;
	height: 60px;
	animation: spin 1s linear infinite;
	box-shadow: 0 4px 12px rgba(062342, 0.2);
	margin-bottom: 1.5rem;
}

.loading-text {
	margin-top: 1rem;
	color: #6c757d;
	font-size: 1rem;
	font-weight: 500;
}

/* Estado de error */
.error-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
	background: white;
	border-radius: 20px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
}

.error-icon {
	font-size: 4rem;
	color: #dc2626;
	margin-bottom: 1rem;
}

.error-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: #1a202c;
	margin-bottom: 0.75rem;
	letter-spacing: -0.5px;
}

.error-description {
	color: #718096;
	font-size: 1.125rem;
	font-weight: 500;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

/* Scrollbar personalizado para la tabla */
.table-scroll-container::-webkit-scrollbar {
	width: 8px;
	height: 8px;
}

.table-scroll-container::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.table-scroll-container::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.table-scroll-container::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* Responsive */
@media (max-width: 1024px) {
	.transacciones-container {
		padding: 1.5rem;
	}

	.table-content {
		max-height: calc(100vh - 200px);
	}
}

@media (max-width: 768px) {
	.transacciones-container {
		padding: 1rem;
	}

	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}

	.table-content {
		padding: 1.5rem;
		max-height: calc(100vh - 180px);
	}

	:deep(.scrollable-table .va-data-table__table thead th),
	:deep(.scrollable-table .va-data-table__table tbody td) {
		padding: 0.75rem 0.5rem;
		font-size: 0.8125rem;
	}
}

@media (max-width: 480px) {
	.transacciones-container {
		padding: 0.75rem;
	}

	.header-gradient {
		padding: 1.25rem;
	}

	.table-content {
		padding: 1rem;
	}

	.type-badge,
	.environment-badge {
		padding: 0.375rem 0.75rem;
		font-size: 0.75rem;
	}
}
</style>
